import { ObjectType } from '@/@types/graphql-types';
import type { Variants } from './types';

type VariantData = Record<string, Variants>;

type CountryDataLoader = () => Promise<{ default: VariantData }>;

type ObjectTypeConfig = Partial<Record<ObjectType, CountryDataLoader>>;

// Configuration for country-specific data loaders
const countryConfig: Record<string, ObjectTypeConfig> = {
	CA: {
		[ObjectType.Movie]: () => import(/* webpackChunkName: "meta-desc-ca-movies" */ './titles/ca-movies'),
		[ObjectType.Show]: () => import(/* webpackChunkName: "meta-desc-ca-shows" */ './titles/ca-shows'),
		[ObjectType.ShowSeason]: () => import(/* webpackChunkName: "meta-desc-ca-seasons" */ './titles/ca-seasons'),
	},
	FR: {
		[ObjectType.Movie]: () => import(/* webpackChunkName: "meta-desc-fr-movies" */ './titles/fr-movies'),
		[ObjectType.Show]: () => import(/* webpackChunkName: "meta-desc-fr-shows" */ './titles/fr-shows'),
		[ObjectType.ShowSeason]: () => import(/* webpackChunkName: "meta-desc-fr-seasons" */ './titles/fr-seasons'),
	},
	GB: {
		[ObjectType.Movie]: () => import(/* webpackChunkName: "meta-desc-gb-movies" */ './titles/uk-movies'),
		[ObjectType.Show]: () => import(/* webpackChunkName: "meta-desc-gb-shows" */ './titles/uk-shows'),
		[ObjectType.ShowSeason]: () => import(/* webpackChunkName: "meta-desc-gb-seasons" */ './titles/uk-seasons'),
	},
};

const cache = new Map<string, VariantData>();

export async function getMetaDescriptionVariants(objectType: ObjectType, country: string): Promise<VariantData | null> {
	const countryData = countryConfig[country];
	if (!countryData) {
		return null;
	}

	const loader = countryData[objectType];
	if (!loader) {
		return null;
	}

	const cacheKey = `${country}-${objectType}`;

	if (!cache.has(cacheKey)) {
		try {
			const module = await loader();
			const data = module.default;
			// as cache is global variant prevent it from getting shared across ssr requests
			if (data) {
				cache.set(cacheKey, data);
			}
		} catch (error) {
			console.error(`Failed to load meta description variants for ${country} ${objectType}:`, error);
			return null;
		}
	}

	return cache.get(cacheKey) || null;
}

export function isSupportedCountry(country: string): boolean {
	return country in countryConfig;
}
